import "react-phone-input-2/lib/style.css";
import classNames from "classnames";
import { useField } from "formik";
import { useState } from "react";
import PhoneInput from "react-phone-input-2";
import { FiPhone } from "react-icons/fi";
import { preloadPhoneUtil } from "../../../validations/invoice";

const SUPPORTED_COUNTRIES = ["co"];

type Props = {
  name: string;
  label: string;
  labeled: boolean;
  disabled?: boolean;
  // Flat variant: clean compact wrapper (white bg, soft gray border, no glass/pill/icon).
  // Used by the desktop "Obtén tu entrada" modal; label rendered ABOVE by the caller.
  flat?: boolean;
};

const PhoneNumberField = (props: Props) => {
  const [field, meta, helper] = useField(props);
  const { value, ...rest } = field;
  const { touched, error } = meta;
  const { setValue, setTouched } = helper;
  const [countryWarning, setCountryWarning] = useState(false);
  const [focused, setFocused] = useState(false);

  const handleChange = (
    value: string,
    country: { countryCode: string; dialCode: any },
    e: any
  ) => {
    const parentElementClassName = e.target?.parentElement?.className;
    const isCountrySelected =
      parentElementClassName === "country" ||
      parentElementClassName === " country-list";
    const isUnsupportedCountry =
      SUPPORTED_COUNTRIES.indexOf(country?.countryCode) === -1;
    if (isCountrySelected && isUnsupportedCountry) {
      setCountryWarning(true);
    }

    setValue({
      number: value,
      countryCode: country.countryCode,
      countryDialCode: country.dialCode,
    });
  };

  const handleBlur = () => {
    setTouched(true);
    setFocused(false);
  };

  const handleFocus = () => {
    setFocused(true);
    // Warm the phone validation library so it's ready by submit time.
    preloadPhoneUtil();
  };

  function _renderHelperText() {
    if (touched && !!error?.length) {
      return (
        <div className="text-red-700 absolute bottom-1 text-xs">{error}</div>
      );
    }
  }

  return (
    <div className={classNames("relative w-full", props.flat ? "pb-[10px]" : "pb-6")}>
      {props.labeled && !props.flat && (
        <label
          className={classNames(
            "absolute -top-2.5 left-4 bg-white/80 backdrop-blur-sm px-2",
            "text-[10px] font-black uppercase tracking-widest",
            "z-10 rounded-full border border-gray-100 flex items-center gap-1.5",
            "transition-colors",
            {
              "text-tikipal-orange": focused,
              "text-gray-400": !focused,
            }
          )}
        >
          <FiPhone size={10} strokeWidth={3} />
          {props.label}
        </label>
      )}
      <div
        className={classNames(
          "overflow-hidden booking-phone-wrap border transition-all duration-300",
          props.flat
            ? classNames("h-[44px] rounded-[12px] booking-phone-flat booking-field-fill", {
                "border-tikipal-orange ring-2 ring-tikipal-orange/15": focused,
                "border-gray-200/70": !focused && !(meta.error && meta.touched),
                "border-red-400": meta.error && meta.touched,
              })
            : classNames(
                "h-14 rounded-[14px] bg-white/10 backdrop-blur-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)]",
                {
                  "border-tikipal-orange/60 ring-4 ring-tikipal-orange/5": focused,
                  "border-white/50": !focused && !(meta.error && meta.touched),
                  "border-red-500": meta.error && meta.touched,
                }
              )
        )}
      >
        <PhoneInput
          {...rest}
          country="co"
          value={value?.number}
          countryCodeEditable={false}
          placeholder="300 123 4567"
          containerStyle={{ width: "100%", height: "100%" }}
          inputStyle={{
            background: "transparent",
            border: "none",
            outline: "none",
            width: "100%",
            height: "100%",
            fontSize: props.flat ? "15px" : "14px",
            color: "#111827",
            fontWeight: "500",
            boxShadow: "none",
            // Flat: la bandera/selector se oculta (ver .booking-phone-flat en CSS), así
            // que el "+57" se alinea con el padding normal del input.
            ...(props.flat ? { paddingLeft: "16px" } : {}),
          }}
          buttonStyle={
            props.flat
              ? { display: "none" }
              : {
                  background: "transparent",
                  border: "none",
                  borderRight: "1px solid rgba(17,24,39,0.1)",
                }
          }
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={props.disabled}
        />
      </div>
      {/* Flat (modal): sin texto de error inline (requisito indicado con asterisco en
          el label). La validación sigue intacta. */}
      {!props.flat && _renderHelperText()}
    </div>
  );
};

export default PhoneNumberField;
